import { JSXElementConstructor, useState } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import ArrowRightIcon from "@atlaskit/icon/glyph/arrow-right";
import type { GlyphProps, CustomGlyphProps } from "@atlaskit/icon/types";
import { token } from "@atlaskit/tokens";

import { ufoEventConfirmationPageTeamTypeSelection } from "@/components/signup/utils";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";
import { wacToken } from "@/libraries/design/foundations";
import { useConfirmationPage } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-confirmation-page.tsx";
import { useScreenManager } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-screen-manager.tsx";
import { updateUserProfile } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/services/update-user-profile";
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";

import { useGupsStatus } from "../../hooks";
import { useUserSegmentationEvents } from "../../hooks/use-user-segmentation-events";
import {
    getIdentityTeamType,
    UserSegmentationTeamTypes
} from "../../models/user-segmentation";
import { getGupsIntegrationInJiraOnboardingEnabled } from "../../utils/get-feature-gate";
import { getCurrentUrlWithParams } from "../../utils/utils";

type UserSegCardProps = {
    readonly card: string;
    readonly Icon:
        | JSXElementConstructor<GlyphProps>
        | JSXElementConstructor<CustomGlyphProps>;
    readonly teamType: UserSegmentationTeamTypes;
    readonly idx: number;
    readonly onClick?: () => void;
    readonly afterClick?: () => void;
    readonly disabled?: boolean;
};

const buttonStyle = css({
    cursor: "pointer",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "16px",
    gap: token("space.100"),
    height: "64px",
    borderRadius: "8px",
    backgroundColor: wacToken("color.neutral.light0"),
    border: `1px solid ${token("color.background.accent.gray.subtler")}`,
    margin: "1px",
    boxSizing: "border-box",
    position: "relative",
    minWidth: "415px",
    [":hover"]: {
        margin: 0,
        border: `2px solid ${token("color.border.brand")}`
    }
});

const iconWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "40px",
    width: "40px",
    backgroundColor: "none"
});

const arrowIconWrapper = css({
    marginInlineStart: "auto",
    marginBlockStart: "5px"
});

const cardHeaderText = css({
    color: wacToken("color.primary.black"),
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "18px",
    fontWeight: "700",
    lineHeight: "28px",
    textAlign: "left"
});

export const UserSegCard = ({
    card,
    Icon,
    teamType,
    onClick,
    afterClick,
    disabled
}: UserSegCardProps) => {
    const {
        userSegmentationContext: {
            closeUserSegmentationUI,
            setSelectedTeamType
        }
    } = useConfirmationPage();

    const { addTeamTypeCallback } = useGupsStatus();

    const { nextScreen } = useScreenManager();

    const stargateClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [isHovering, setIsHovering] = useState(false);
    const { fireTeamTypeProcessedEvent } = useUserSegmentationEvents();

    const onClickHandler = async () => {
        try {
            ufoEventConfirmationPageTeamTypeSelection.start();
            ufoEventConfirmationPageTeamTypeSelection.setCustomData({
                teamType: teamType
            });
            onClick?.();

            if (getGupsIntegrationInJiraOnboardingEnabled()) {
                addTeamTypeCallback(teamType);
            }

            if (teamType == undefined) {
                ufoEventConfirmationPageTeamTypeSelection.failure({
                    error: new Error("teamType is undefined")
                });
                return;
            }

            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "button",
                actionSubjectId:
                    TRACKING_EVENT_ACTION_SUBJECT_IDS.teamFocusTeamTypeButton,
                attributes: {
                    atlasTicket: "ATLAS-69623",
                    teamType: teamType
                }
            }).fire();

            // map the selected team type to the value
            // downstream consumers are expecting.
            const teamTypeFromIdentity = getIdentityTeamType(teamType);

            await updateUserProfile({
                selectedTeamType: teamTypeFromIdentity,
                stargateClient
            });
            setSelectedTeamType(teamType);
            fireTeamTypeProcessedEvent(teamType);

            // add team_type to query param to maintain selection on refresh
            const updatedUrl = getCurrentUrlWithParams({
                team_type: teamType || ""
            });
            history.replaceState({}, "", updatedUrl);

            closeUserSegmentationUI(); // TODO: remove closeUserSegmentationUI once confirmation page rearch is productionized.
            nextScreen();
            ufoEventConfirmationPageTeamTypeSelection.success();
        } catch (e) {
            ufoEventConfirmationPageTeamTypeSelection.failure({
                error: e
            });
        } finally {
            afterClick?.();
        }
    };

    return (
        <button
            data-testid="team-type-button"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onFocus={() => setIsHovering(true)}
            onBlur={() => setIsHovering(false)}
            css={buttonStyle}
            onClick={onClickHandler}
            disabled={disabled}
        >
            <div css={iconWrapper}>
                <Icon label="" primaryColor={"#FFF"} size="large" />
            </div>
            <span css={cardHeaderText}>{card}</span>
            {isHovering && (
                <div css={arrowIconWrapper} data-testid="arrow-icon">
                    <ArrowRightIcon label="arrow-right" size="medium" />
                </div>
            )}
        </button>
    );
};
